import React from 'react';
import { ArrowRightIcon, SparklesIcon } from 'lucide-react';
import { Avatar } from '../Avatar';
import { BrandMark } from '../BrandMark';
import { HiveGrid } from '../engagement/HiveGrid';
import { FilterChip } from '../FilterChip';
import { communities } from '../../data/communities';
import { people } from '../../data/people';
import { beeArchetypes } from '../../data/recapArchetypes';
import { useLocale } from '../../contexts/LocaleContext';
import type { BeeArchetypeId, Recap } from '../../types/engagement';
import { hiveGridCells, hiveSnapshot, percentileEligible, rankedHabits, socialTotal, strongestHabits } from '../../utils/recap';

/** Shared story surface. Dark for celebratory moments, light for the hive and details. */
export function StoryCard({
  tone = 'light',
  overline,
  children
}: {
  tone?: 'light' | 'dark';
  overline?: string;
  children: React.ReactNode;
}) {
  const dark = tone === 'dark';
  return <article className={`flex min-h-[440px] flex-col rounded-panel p-3 ${dark ? 'bg-deep text-sand' : 'border border-sand-line bg-white/80 text-deep'}`} data-id="element-1296" data-mp="34dqts3r7zoq6">
      {overline && <p className={`bee-overline text-[9px] font-bold ${dark ? 'text-gold' : 'text-muted'}`} data-id="element-1297" data-mp="7g0c075qgd1c">{overline}</p>}
      <div className="flex flex-1 flex-col justify-center" data-id="element-1298" data-mp="bfiyv8h46y6t">{children}</div>
    </article>;
}
export function IntroCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t,
    tl
  } = useLocale();
  return <StoryCard tone="dark" data-id="element-1299" data-mp="l99mkb5f34lg">
      <BrandMark size={40} data-id="element-1300" data-mp="q601gv5hju5a" />
      <p className="bee-overline mt-3 text-[9px] font-bold text-ondark" data-id="element-1301" data-mp="q56x20u01gqb">{tl(recap.periodLabel)}</p>
      <h2 className="mt-1 text-[30px] font-bold leading-tight text-sand" data-id="element-1302" data-mp="v6e581020hxi">
        {recap.period === 'month' ? t('eng.recap.intro.month') : t('eng.recap.intro.year')}
      </h2>
      <p className="mt-2 max-w-[30ch] text-[14px] leading-relaxed text-ondark" data-id="element-1303" data-mp="2wx7q93st9rcw">{t('eng.recap.intro.body')}</p>
    </StoryCard>;
}
export function BeeTypeCard({
  recap,
  archetype
}: {
  recap: Recap;
  archetype: BeeArchetypeId;
}) {
  const {
    t,
    tl
  } = useLocale();
  const content = beeArchetypes[archetype];
  return <StoryCard tone="dark" overline={recap.period === 'year' ? t('eng.recap.bee.overlineYear') : t('eng.recap.bee.overline')} data-id="element-1304" data-mp="35ro2hrbargq">
      <span className="grid h-16 w-16 place-items-center rounded-pill border border-gold/50" data-id="element-1305" data-mp="h2encr6qltpt">
        <BrandMark size={34} data-id="element-1306" data-mp="d50fb2uslrze" />
      </span>
      <h2 className="mt-3 text-[28px] font-bold leading-tight text-gold" data-id="element-1307" data-mp="r25j0w71aerh">{tl(content.name)}</h2>
      <p className="mt-2 text-[14px] leading-relaxed text-sand" data-id="element-1308" data-mp="m43zka5p4dfh">{tl(content.description)}</p>
      <p className="mt-3 border-t border-sand/15 pt-2 text-[11.5px] leading-snug text-ondark" data-id="element-1309" data-mp="10s8fx7ha0cv3">
        {t('eng.recap.bee.basis')} {t('eng.recap.bee.note')}
      </p>
    </StoryCard>;
}
export function HiveEvolutionCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t,
    tl
  } = useLocale();
  const start = hiveSnapshot(recap.hiveStartBee);
  const end = hiveSnapshot(recap.hiveEndBee);
  const total = hiveGridCells(end);
  const evolved = start.stage.id !== end.stage.id;
  const panes = [{
    label: t('eng.recap.hive.start'),
    snap: start
  }, {
    label: t('eng.recap.hive.end'),
    snap: end
  }];
  return <StoryCard overline={t('eng.recap.hive.overline')} data-id="element-1310" data-mp="1ffqdzn0c298j">
      <p className="bee-num text-[26px] font-bold leading-tight text-deep" data-id="element-1311" data-mp="1fh9vti1n0scf">
        {t('eng.recap.hive.delta', {
        from: start.cells,
        to: end.cells
      })}
      </p>
      <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2" data-id="element-1312" data-mp="15ocjxur5i1ms">
        {panes.map((pane, index) => <React.Fragment key={pane.label} data-id="element-1313" data-mp="1ahowv7mgwjzp" data-mpi={index}>
            {index === 1 && <ArrowRightIcon size={18} className="text-gold-deep" aria-hidden="true" data-id="element-1314" data-mp="vrpzh72qlcla" />}
            <figure className="min-w-0" data-id="element-1315" data-mp="10q1a3k0uvr4k">
              <HiveGrid filled={pane.snap.cells} total={total} columns={8} data-id="element-1316" data-mp="10r2onyh2yvr1" />
              <figcaption className="mt-1" data-id="element-1317" data-mp="mq9oax43l6g0">
                <span className="block text-[11px] text-muted" data-id="element-1318" data-mp="mrba9wmnjdci">{pane.label}</span>
                <span className="block text-[12.5px] font-bold text-deep" data-id="element-1319" data-mp="rp35azilffkx">{tl(pane.snap.stage.name)}</span>
                <span className="bee-num block text-[11.5px] text-muted" data-id="element-1320" data-mp="jo588y25jidu">
                  {t('eng.recap.hive.cells', {
                n: pane.snap.cells
              })}
                </span>
              </figcaption>
            </figure>
          </React.Fragment>)}
      </div>
      <p className={`mt-3 flex items-start gap-2 rounded-card px-2 py-2 text-[13px] font-medium ${evolved ? 'border border-gold/60 bg-gold/15 text-deep' : 'bg-sand-deep/70 text-deep'}`} data-id="element-1321" data-mp="5ybjefqncy44">
        {evolved && <SparklesIcon size={16} className="mt-[1px] shrink-0 text-gold-deep" aria-hidden="true" data-id="element-1322" data-mp="3s35h89ppa0bp" />}
        {evolved ? t('eng.recap.hive.evolved', {
        from: tl(start.stage.name),
        to: tl(end.stage.name)
      }) : t('eng.recap.hive.same', {
        stage: tl(end.stage.name)
      })}
      </p>
    </StoryCard>;
}
export function ConsistencyCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t
  } = useLocale();
  const ratio = recap.checkDays / recap.daysInPeriod;
  return <StoryCard overline={t('eng.recap.consistency.overline')} data-id="element-1323" data-mp="2zb3inygig2rc">
      <p className="bee-num text-[64px] font-bold leading-none text-deep" data-id="element-1324" data-mp="349eugeqd8nc7">{recap.checkDays}</p>
      <p className="mt-1 text-[14px] text-deep" data-id="element-1325" data-mp="34723jc2mr2n0">
        {t('eng.recap.consistency.days', {
        n: recap.checkDays,
        total: recap.daysInPeriod
      })}
      </p>
      <div className="mt-2 h-[6px] overflow-hidden rounded-pill bg-sand-deep" aria-hidden="true" data-id="element-1326" data-mp="3d0nk90ycutz2">
        <div className="h-full rounded-pill bg-gold" style={{
        width: `${Math.min(1, ratio) * 100}%`
      }} data-id="element-1327" data-mp="2gchg0f5ci3lb" />
      </div>
      <ul className="mt-3 space-y-1 text-[13px] text-muted" data-id="element-1328" data-mp="2lb2hsj08wvbv">
        <li className="bee-num" data-id="element-1329" data-mp="2pbmdrj931swf">{t('eng.recap.consistency.streak', {
          n: recap.longestStreak
        })}</li>
        <li className="bee-num" data-id="element-1330" data-mp="2ubh7duk8swl6">{t('eng.recap.consistency.avg', {
          n: recap.averageLifeScore
        })}</li>
      </ul>
    </StoryCard>;
}
export function HabitCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t
  } = useLocale();
  const top = strongestHabits(recap);
  const ranked = rankedHabits(recap);
  const name = (id: string) => t(`habit.${id}.title`);
  return <StoryCard overline={t('eng.recap.habit.overline')} data-id="element-1331" data-mp="25q7m6skg4qri">
      <h2 className="text-[26px] font-bold leading-tight text-deep" data-id="element-1332" data-mp="21lht1bh69qaa">
        {top.length > 1 ? t('eng.recap.habit.tied', {
        a: name(top[0]),
        b: name(top[1])
      }) : name(top[0])}
      </h2>
      <p className="bee-num mt-1 text-[13px] text-muted" data-id="element-1333" data-mp="2amy13ckilgbc">
        {t('eng.recap.habit.ratio', {
        n: Math.round(ranked[0].ratio * 100)
      })}
      </p>
      <ul className="mt-3 space-y-2" data-id="element-1334" data-mp="1mvk957q9zmqw">
        {ranked.map(item => <li key={item.habit} data-id="element-1335" data-mp="1hzv4ipzeffnq">
            <span className="flex justify-between text-[12px] text-deep" data-id="element-1336" data-mp="1hwh6ftxrpsm3">
              {name(item.habit)}
              <span className="bee-num text-muted" data-id="element-1337" data-mp="1wp9dvy2z5x34">{Math.round(item.ratio * 100)}%</span>
            </span>
            <span className="mt-[3px] block h-[4px] overflow-hidden rounded-pill bg-sand-deep" aria-hidden="true" data-id="element-1338" data-mp="1475iwmns8cr5">
              <span className={`block h-full rounded-pill ${top.includes(item.habit) ? 'bg-gold' : 'bg-soft'}`} style={{
            width: `${item.ratio * 100}%`
          }} data-id="element-1339" data-mp="z8khwl0php1n" />
            </span>
          </li>)}
      </ul>
    </StoryCard>;
}
export function ActivityCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t,
    tl
  } = useLocale();
  return <StoryCard overline={t('eng.recap.activity.overline')} data-id="element-1340" data-mp="35wb36i4o7ucm">
      <h2 className="text-[34px] font-bold leading-tight text-deep" data-id="element-1341" data-mp="35wujxvil4cp2">{tl(recap.mainActivity)}</h2>
      <p className="mt-2 text-[14px] leading-relaxed text-muted" data-id="element-1342" data-mp="3tmf76qlrtw45">{t('eng.recap.activity.body')}</p>
    </StoryCard>;
}
export function CommunityCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t
  } = useLocale();
  return <StoryCard overline={t('eng.recap.community.overline')} data-id="element-1343" data-mp="3knbkzdrb50lf">
      <p className="bee-num text-[34px] font-bold leading-tight text-deep" data-id="element-1344" data-mp="3pj8zrfmx9yw3">
        {t('eng.recap.community.posts', {
        n: recap.communityPosts
      })}
      </p>
      <p className="bee-num mt-1 text-[18px] font-medium text-deep" data-id="element-1345" data-mp="2e55bmq30n4yy">
        {t('eng.recap.community.challenges', {
        n: recap.challengesDone
      })}
      </p>
    </StoryCard>;
}
export function SocialCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t
  } = useLocale();
  const person = people.find(item => item.id === recap.socialHighlight?.personId);
  if (!person) return null;
  const first = person.name.split(' ')[0];
  return <StoryCard tone="dark" overline={t('eng.recap.social.overline')} data-id="element-1346" data-mp="2qw8wxay4yn41">
      <Avatar src={person.avatar} name={person.name} size={72} data-id="element-1347" data-mp="3oj7v5q4h8341" />
      <h2 className="mt-3 text-[24px] font-bold leading-tight text-sand" data-id="element-1348" data-mp="3oiem2ii1p1iq">
        {recap.period === 'month' ? t('eng.recap.social.month', {
        name: first
      }) : t('eng.recap.social.year', {
        name: first
      })}
      </h2>
      <p className="bee-num mt-1 text-[14px] text-gold" data-id="element-1349" data-mp="39ubdd1cmbbq8">{t('eng.recap.social.count', {
        n: socialTotal(recap)
      })}</p>
      <p className="mt-3 text-[11.5px] text-ondark" data-id="element-1350" data-mp="b4te1aqze5y0">{t('eng.recap.social.note')}</p>
    </StoryCard>;
}
export function PercentileCard({
  recap,
  communityId,
  onChoose
}: {
  recap: Recap;
  communityId: string;
  onChoose: (id: string) => void;
}) {
  const {
    t,
    tl
  } = useLocale();
  const context = recap.communityContext.find(item => item.communityId === communityId);
  const community = communities.find(item => item.id === communityId);
  const name = community ? tl(community.name) : communityId;
  const eligible = percentileEligible(context);
  return <StoryCard overline={t('eng.recap.pct.overline')} data-id="element-1351" data-mp="sjxdul9yd40e">
      {eligible && context ? <>
          <p className="bee-num text-[56px] font-bold leading-none text-deep" data-id="element-1352" data-mp="wlr8ocxgvdsn">{context.topPercent}%</p>
          <p className="mt-2 text-[15px] leading-snug text-deep" data-id="element-1353" data-mp="11kbz5kmx2j3w">
            {recap.period === 'month' ? t('eng.recap.pct.month', {
          n: context.topPercent,
          community: name
        }) : t('eng.recap.pct.year', {
          n: context.topPercent,
          community: name
        })}
          </p>
        </> : <p className="text-[15px] leading-snug text-deep" data-id="element-1354" data-mp="1kd65jq6h52wi">{t('eng.recap.pct.insufficient')}</p>}
      {recap.communityContext.length > 1 && <div className="mt-3" data-id="element-1355" data-mp="7uk41aqqfyxp">
          <p className="text-[11.5px] font-medium text-muted" data-id="element-1356" data-mp="7reanjb2maw6">{t('eng.recap.pct.choose')}</p>
          <div className="mt-1 flex flex-wrap gap-1" data-id="element-1357" data-mp="cq955iiehnw4">
            {recap.communityContext.map(item => {
          const c = communities.find(entry => entry.id === item.communityId);
          return <FilterChip key={item.communityId} label={c ? tl(c.name) : item.communityId} active={item.communityId === communityId} onClick={() => onChoose(item.communityId)} data-id="element-1358" data-mp="rh8amknxvwbh" />;
        })}
          </div>
        </div>}
      <p className="mt-3 text-[11.5px] leading-snug text-muted" data-id="element-1359" data-mp="15h9bw18495rh">{t('eng.recap.pct.note')}</p>
    </StoryCard>;
}
export function MilestonesCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t,
    tl
  } = useLocale();
  return <StoryCard tone="dark" overline={t('eng.recap.milestones.overline')} data-id="element-1360" data-mp="3795kp32wg1ik">
      <ul className="space-y-3" data-id="element-1361" data-mp="2zp3b6rpxwbkc">
        {recap.highlights.map(item => <li key={tl(item.label)} className="border-b border-sand/15 pb-2 last:border-b-0" data-id="element-1362" data-mp="2uyekvfpzxkms">
            <span className="block text-[11.5px] text-ondark" data-id="element-1363" data-mp="2pz1pg6l2758l">{tl(item.label)}</span>
            <span className="block text-[17px] font-bold text-sand" data-id="element-1364" data-mp="2pwp73sbe2sv0">{tl(item.value)}</span>
          </li>)}
      </ul>
    </StoryCard>;
}